/**
 * ============================================================
 * PGLP | MONTE HERMOM
 * SERVICE: DiligenciaService
 * VERSÃO:  12.0.0
 * ------------------------------------------------------------
 * NOVIDADES v12.0.0:
 *   ✔ buscarTarefasBackend() — lista tarefas do servidor
 *   ✔ concluirTarefaBackend() — conclui + audit
 *   ✔ reabrirTarefaBackend() — reabre com motivo
 *   ✔ Sincronização híbrida cache ↔ backend
 * ============================================================
 */

import { calcularProximaTentativa } from './prazos/operacional.engine';
import { laudoApi } from './laudoApi';

const KEY = 'pglp_diligencias';
const KEY_TAREFAS = 'pglp_tarefas';

/* =========================
   CONSTANTES
========================= */

export const CATEGORIA = {
  CONTATO:     'CONTATO',
  DIVERGENCIA: 'DIVERGENCIA',
  VERIFICACAO: 'VERIFICACAO',
  LEMBRETE:    'LEMBRETE',
  MANUAL:      'MANUAL',
  SISTEMA:     'SISTEMA',
};

export const PRIORIDADE = {
  SEM_PRIORIDADE: 'SEM_PRIORIDADE',
  BAIXA:          'BAIXA',
  MEDIA:          'MEDIA',
  ALTA:           'ALTA',
  ALTISSIMA:      'ALTISSIMA',
};

export const PESO_PRIORIDADE = {
  SEM_PRIORIDADE: 0,
  BAIXA:          1,
  MEDIA:          2,
  ALTA:           3,
  ALTISSIMA:      4,
};

/* =========================
   STORAGE LOCAL
========================= */

function getAll() {
  try { return JSON.parse(localStorage.getItem(KEY)) || []; }
  catch { return []; }
}
function saveAll(d) { localStorage.setItem(KEY, JSON.stringify(d)); }
function getTarefas() {
  try { return JSON.parse(localStorage.getItem(KEY_TAREFAS)) || []; }
  catch { return []; }
}
function saveTarefas(d) { localStorage.setItem(KEY_TAREFAS, JSON.stringify(d)); }

/* =========================
   SINCRONIZAÇÃO
========================= */

async function sincronizarTarefa(t) {
  try {
    await laudoApi.salvarModulo({
      tipo_modulo: 'TAREFA',
      dados: {
        id: t.id, laudo_id: t.laudoId, laudo_uuid: t.laudoUuid || null,
        imovel_id: t.imovelId || null, imovel_uuid: t.imovelUuid || null,
        titulo: t.titulo, descricao: t.descricao,
        origem: t.origem, categoria: t.categoria, prioridade: t.prioridade,
        status: t.status, prazo: t.prazo, criado_em: t.criadoEm,
      },
    });
    return { ok: true, offline: false };
  } catch (err) {
    return { ok: false, offline: !!err._enfileirado, erro: err.message };
  }
}

async function sincronizarEvento(e) {
  try {
    await laudoApi.salvarModulo({
      tipo_modulo: 'DILIGENCIA_EVENTO',
      dados: {
        laudo_id: e.ordemId, laudo_uuid: e.laudoUuid || null,
        tipo: e.tipo, meio: e.meio || null,
        descricao: e.descricao, resultado: e.resultado || null,
        destinatario: e.destinatario || null, data_hora: e.dataHora,
      },
    });
    return { ok: true, offline: false };
  } catch (err) {
    return { ok: false, offline: !!err._enfileirado, erro: err.message };
  }
}

/* =========================
   STATUS MAP
========================= */

function mapStatus(resultado) {
  switch (resultado) {
    case 'REALIZADA':   return 'CONCLUIDA';
    case 'AUSENTE':     return 'REAGENDAR';
    case 'IMPOSSIVEL':  return 'IMPEDIDA';
    case 'COMUNICACAO': return 'EM_ANDAMENTO';
    default:            return 'PENDENTE';
  }
}

/* =========================
   ORDENAÇÃO
========================= */

export function ordenarPorPrioridade(tarefas = []) {
  return [...tarefas].sort((a, b) => {
    const pa = PESO_PRIORIDADE[a.prioridade] ?? 0;
    const pb = PESO_PRIORIDADE[b.prioridade] ?? 0;
    if (pb !== pa) return pb - pa;
    return new Date(a.prazo || 0) - new Date(b.prazo || 0);
  });
}

/* =========================
   TAREFA (base)
========================= */

async function criarTarefa({
  ordemId, laudoUuid = null, imovelId = null, imovelUuid = null,
  tipo, descricao,
  prazo = new Date().toISOString(),
  origem = 'DILIGENCIA',
  categoria = CATEGORIA.CONTATO,
  prioridade = PRIORIDADE.SEM_PRIORIDADE,
  status = 'PENDENTE',
}) {
  const nova = {
    id: Date.now(),
    laudoId: ordemId, laudoUuid, imovelId, imovelUuid,
    titulo: tipo, descricao, prazo, status, origem, categoria, prioridade,
    criadoEm: new Date().toISOString(),
  };

  const sync = await sincronizarTarefa(nova);
  nova._offline = sync.offline;

  const tarefas = getTarefas();
  tarefas.push(nova);
  saveTarefas(tarefas);
  return nova;
}

async function atualizarStatusTarefa(ordemId, novoStatus, filtroCategoria = null) {
  const tarefas = getTarefas();
  const atualizadas = tarefas.map(t => {
    if (t.laudoId !== ordemId) return t;
    if (filtroCategoria && t.categoria !== filtroCategoria) return t;
    return { ...t, status: novoStatus, atualizadoEm: new Date().toISOString() };
  });
  saveTarefas(atualizadas);

  const alteradas = atualizadas.filter(t =>
    t.laudoId === ordemId && (!filtroCategoria || t.categoria === filtroCategoria)
  );
  await Promise.all(alteradas.map(sincronizarTarefa));
}

/* =========================
   LISTAR (cache local)
========================= */

export function listarDiligencias(ordemId, opts = {}) {
  const { imovelId = null, imovelUuid = null, categoria = null } = opts;
  const filtradas = getAll().filter(d => {
    if (d.ordemId !== ordemId && d.laudoId !== ordemId) return false;
    if (imovelId !== null && d.imovelId !== imovelId) return false;
    if (imovelUuid !== null && d.imovelUuid !== imovelUuid) return false;
    if (categoria !== null && d.categoria !== categoria) return false;
    return true;
  });
  return ordenarPorPrioridade(filtradas);
}

export function listarTarefasImovel(ordemId, imovelId) {
  const tarefas = getTarefas().filter(t =>
    t.laudoId === ordemId && t.imovelId === imovelId &&
    !['CANCELADA','CONCLUIDA'].includes(t.status)
  );
  return ordenarPorPrioridade(tarefas);
}

/* =========================
   BUSCAR DO BACKEND
========================= */

/**
 * Busca tarefas do servidor e sincroniza o cache local.
 * @param {string} codigo — ex: 'MH-2026-0006'
 * @param {object} filtros — { imovel_id, imovel_uuid, categoria, status, prioridade, limit }
 */
export async function buscarTarefasBackend(codigo, filtros = {}) {
  if (!codigo) throw new Error('buscarTarefasBackend: código obrigatório');

  const resp = await laudoApi.listarTarefas(codigo, filtros);
  const tarefasRemotas = resp.tarefas || [];

  // Sincroniza cache local: substitui tarefas do laudo pelas do servidor
  const laudoId = resp.laudo_id;
  const cacheAtual = getTarefas();
  const outras = cacheAtual.filter(t => t.laudoId !== laudoId);

  const cacheAtualizado = tarefasRemotas.map(t => ({
    id: t.id,
    laudoId: t.laudo_id,
    laudoUuid: t.laudo_uuid,
    imovelId: t.imovel_id,
    imovelUuid: t.imovel_uuid,
    titulo: t.titulo,
    descricao: t.descricao,
    origem: t.origem,
    categoria: t.categoria,
    prioridade: t.prioridade,
    status: t.status,
    prazo: t.prazo_real,
    executadoEm: t.executado_em,
    erro: t.erro,
    criadoEm: t.criado_em,
    _server: true,
  }));

  saveTarefas([...outras, ...cacheAtualizado]);

  return {
    ...resp,
    tarefas: cacheAtualizado,
  };
}

/* =========================
   CONCLUIR TAREFA (backend)
========================= */

export async function concluirTarefa(tarefaId, observacao = '') {
  if (!tarefaId) throw new Error('concluirTarefa: tarefaId obrigatório');

  const resp = await laudoApi.concluirTarefa(tarefaId, observacao);

  // Espelha no cache
  const tarefas = getTarefas();
  const idx = tarefas.findIndex(t => t.id === tarefaId);
  if (idx >= 0) {
    tarefas[idx] = {
      ...tarefas[idx],
      status: 'CONCLUIDA',
      executadoEm: resp.executado_em,
      erro: null,
    };
    saveTarefas(tarefas);
  }

  return resp;
}

/* =========================
   REABRIR TAREFA (backend)
========================= */

export async function reabrirTarefa(tarefaId, motivo) {
  if (!tarefaId) throw new Error('reabrirTarefa: tarefaId obrigatório');
  if (!motivo)   throw new Error('reabrirTarefa: motivo obrigatório');

  const resp = await laudoApi.reabrirTarefa(tarefaId, motivo);

  const tarefas = getTarefas();
  const idx = tarefas.findIndex(t => t.id === tarefaId);
  if (idx >= 0) {
    tarefas[idx] = {
      ...tarefas[idx],
      status: 'PENDENTE',
      executadoEm: null,
      erro: null,
    };
    saveTarefas(tarefas);
  }

  return resp;
}

/* =========================
   COMUNICAÇÃO
========================= */

async function registrarComunicacao(ordemId, meio, mensagem, laudoUuid = null) {
  const evento = {
    id: Date.now(), ordemId, laudoUuid,
    tipo: 'COMUNICACAO', meio,
    dataHora: new Date().toISOString(),
    descricao: mensagem, resultado: null,
  };
  const dados = getAll();
  dados.push(evento);
  saveAll(dados);
  await sincronizarEvento(evento);
  return evento;
}

/* =========================
   EXECUÇÃO AUTOMÁTICA
========================= */

export async function executarTarefasAutomaticas() {
  const tarefas = getTarefas();
  const agora = new Date();
  const paraExecutar = tarefas.filter(t =>
    t.categoria === CATEGORIA.CONTATO && t.status === 'PENDENTE' &&
    new Date(t.prazo) <= agora
  );

  for (const t of paraExecutar) {
    await registrarComunicacao(t.laudoId, 'EMAIL',
      'Envio automático executado pelo sistema', t.laudoUuid);
    t.status = 'CONCLUIDA';
    t.executadoEm = new Date().toISOString();
    await sincronizarTarefa(t);
  }
  saveTarefas(tarefas);
}

/* =========================
   REGISTRAR TENTATIVA
========================= */

export async function registrarTentativa(
  ordemId, payload,
  { laudoUuid = null, imovelId = null, imovelUuid = null } = {}
) {
  if (!ordemId || !payload?.resultado) {
    throw new Error('registrarTentativa: ordemId e payload.resultado são obrigatórios');
  }

  const agora = new Date();
  const tentativa = {
    id: Date.now(), ordemId, laudoUuid, imovelId, imovelUuid,
    tipo: 'TENTATIVA',
    meio: payload.meio || null,
    resultado: payload.resultado,
    destinatario: payload.destinatario || null,
    dataHora: agora.toISOString(),
    descricao: payload.observacao || '',
  };

  const dados = getAll();
  dados.push(tentativa);
  saveAll(dados);
  await sincronizarEvento(tentativa);

  const tarefaBase = await criarTarefa({
    ordemId, laudoUuid, imovelId, imovelUuid,
    tipo: 'Diligência',
    descricao: `Resultado: ${payload.resultado}`,
    origem: 'DILIGENCIA',
    categoria: CATEGORIA.VERIFICACAO,
    prioridade: PRIORIDADE.MEDIA,
  });

  await atualizarStatusTarefa(tarefaBase.laudoId, mapStatus(payload.resultado));

  if (!payload.agendado) {
    const proximaAuto = calcularProximaTentativa({
      dataBase: agora, horas: 8, tipo: 'AUTOMATICO',
    });
    const proximaManual = calcularProximaTentativa({
      dataBase: agora, horas: 8, tipo: 'MANUAL',
    });

    await criarTarefa({
      ordemId, laudoUuid, imovelId, imovelUuid,
      tipo: 'Contato automático',
      descricao: 'Envio automático (email)',
      prazo: proximaAuto.toISOString(),
      origem: 'DILIGENCIA_AUTO',
      categoria: CATEGORIA.CONTATO,
      prioridade: PRIORIDADE.ALTA,
    });

    await criarTarefa({
      ordemId, laudoUuid, imovelId, imovelUuid,
      tipo: 'Contato manual',
      descricao: 'Contato WhatsApp / telefone',
      prazo: proximaManual.toISOString(),
      origem: 'DILIGENCIA_MANUAL',
      categoria: CATEGORIA.CONTATO,
      prioridade: PRIORIDADE.ALTA,
    });
  }

  const textosCertidao = {
    AUSENTE:    'Certifico que não havia responsável no local.',
    IMPOSSIVEL: 'Certifico que foi impossível realizar a vistoria.',
    REALIZADA:  'Certifico que a vistoria foi realizada.',
  };
  if (textosCertidao[payload.resultado]) {
    const certidao = {
      id: Date.now() + 1,
      ordemId, laudoUuid, imovelId, imovelUuid,
      tipo: 'CERTIDAO', meio: null, resultado: null,
      dataHora: new Date().toISOString(),
      descricao: textosCertidao[payload.resultado],
    };
    dados.push(certidao);
    saveAll(dados);
    await sincronizarEvento(certidao);
  }

  return tentativa;
}

/* =========================
   GARANTIR CONTATO INICIAL
========================= */

export async function garantirContatoInicial({
  ordemId, laudoUuid = null, imovelId = null, imovelUuid = null, prazo = null,
}) {
  if (!ordemId) throw new Error('garantirContatoInicial: ordemId obrigatório');

  const existentes = getTarefas().filter(t =>
    t.laudoId === ordemId &&
    (imovelId === null || t.imovelId === imovelId) &&
    t.categoria === CATEGORIA.CONTATO &&
    ['PENDENTE','REAGENDAR','EM_ANDAMENTO'].includes(t.status)
  );
  if (existentes.length > 0) {
    return { criado: false, motivo: 'Já existe contato pendente' };
  }

  const resp = await laudoApi.salvarModulo({
    tipo_modulo: 'GARANTIR_CONTATO',
    dados: {
      laudo_id: ordemId, laudo_uuid: laudoUuid,
      imovel_id: imovelId, imovel_uuid: imovelUuid,
      prazo: prazo || new Date(Date.now() + 3600000).toISOString(),
    },
  });

  if (resp.criado) {
    const tarefas = getTarefas();
    tarefas.push({
      id: resp.tarefa_id || Date.now(),
      laudoId: ordemId, laudoUuid, imovelId, imovelUuid,
      titulo: 'Contato inicial',
      descricao: 'Tarefa criada automaticamente: sem contato pendente.',
      prazo: prazo || new Date(Date.now() + 3600000).toISOString(),
      status: 'PENDENTE',
      origem: 'DILIGENCIA_AUTO',
      categoria: CATEGORIA.CONTATO,
      prioridade: PRIORIDADE.ALTA,
      criadoEm: new Date().toISOString(),
    });
    saveTarefas(tarefas);
  }

  return resp;
}

/* =========================
   DETECTAR DIVERGÊNCIA
========================= */

export async function detectarDivergencia({
  ordemId, laudoUuid = null, imovelId, imovelUuid,
  tipo, campo = '', valorInformado = '', valorOficial = '',
  severidade = 'ALERTA', descricao = '', fonte = 'PLANEJAMENTO',
}) {
  if (!ordemId || !imovelId) throw new Error('detectarDivergencia: ordemId e imovelId são obrigatórios');
  if (!tipo) throw new Error('detectarDivergencia: tipo obrigatório');

  const resp = await laudoApi.salvarModulo({
    tipo_modulo: 'DIVERGENCIA_DETECTADA',
    dados: {
      laudo_id: ordemId, laudo_uuid: laudoUuid,
      imovel_id: imovelId, imovel_uuid: imovelUuid,
      tipo, campo,
      valor_informado: valorInformado,
      valor_oficial: valorOficial,
      severidade, descricao, fonte,
    },
  });

  const prioridade = resp?.prioridade || (
    severidade === 'CRITICO' ? PRIORIDADE.ALTISSIMA :
    severidade === 'ALERTA'  ? PRIORIDADE.ALTA :
    PRIORIDADE.MEDIA
  );

  const tarefas = getTarefas();
  tarefas.push({
    id: resp.tarefa_id || Date.now(),
    laudoId: ordemId, laudoUuid, imovelId, imovelUuid,
    titulo: `Checar divergência: ${tipo}`,
    descricao: descricao || `${campo}: ${valorInformado} ≠ ${valorOficial}`,
    prazo: new Date().toISOString(),
    status: 'PENDENTE',
    origem: 'DIVERGENCIA',
    categoria: CATEGORIA.DIVERGENCIA,
    prioridade,
    criadoEm: new Date().toISOString(),
  });
  saveTarefas(tarefas);

  return resp;
}

/* =========================
   CRIAR TAREFA MANUAL
========================= */

export async function criarTarefaManual({
  ordemId, laudoUuid = null, imovelId = null, imovelUuid = null,
  titulo, descricao, prazo = null, prioridade = PRIORIDADE.MEDIA,
}) {
  if (!ordemId || !titulo) throw new Error('criarTarefaManual: ordemId e titulo obrigatórios');
  return criarTarefa({
    ordemId, laudoUuid, imovelId, imovelUuid,
    tipo: titulo, descricao: descricao || '',
    prazo: prazo || new Date().toISOString(),
    origem: 'MANUAL', categoria: CATEGORIA.MANUAL, prioridade,
  });
}

/* =========================
   AGENDAR VISTORIA
========================= */

export async function agendarVistoria(ordemId, imovelId, dataHora, opts = {}) {
  if (!ordemId || !imovelId || !dataHora) {
    throw new Error('agendarVistoria: ordemId, imovelId e dataHora são obrigatórios');
  }

  const {
    laudoUuid = null, imovelUuid = null,
    local = '', responsavel = '', observacao = '',
    meioConfirmacao = 'WHATSAPP',
    endereco = '', processo = '', titulo = '',
  } = opts;

  const dataISO = dataHora instanceof Date ? dataHora.toISOString() : String(dataHora);

  const resp = await laudoApi.salvarModulo({
    tipo_modulo: 'AGENDAMENTO_VISTORIA',
    dados: {
      laudo_id: ordemId, laudo_uuid: laudoUuid,
      imovel_id: imovelId, imovel_uuid: imovelUuid,
      data_agendada: dataISO,
      local, responsavel, observacao,
      meio_confirmacao: meioConfirmacao,
      endereco, processo, titulo,
    },
  });

  const tarefas = getTarefas();
  const agoraISO = new Date().toISOString();
  let canceladasLocal = 0;

  const atualizadas = tarefas.map(t => {
    if (t.laudoId !== ordemId) return t;
    if (t.imovelId !== imovelId) return t;
    if (t.categoria !== CATEGORIA.CONTATO) return t;
    if (!['PENDENTE','REAGENDAR'].includes(t.status)) return t;
    canceladasLocal++;
    return {
      ...t, status: 'CANCELADA', executadoEm: agoraISO,
      erro: `Cancelada: vistoria agendada para ${dataISO}`,
    };
  });
  saveTarefas(atualizadas);

  return {
    ok: true,
    vistoria_id: resp?.vistoria_id ?? null,
    agendamento_id: resp?.agendamento_id ?? null,
    data_agendada: resp?.data_agendada ?? dataISO,
    imovel_id: imovelId,
    tarefas_canceladas: resp?.tarefas_canceladas ?? canceladasLocal,
  };
}

/* =========================
   TIMELINE
========================= */

export async function carregarTimeline(codigo) {
  if (!codigo) throw new Error('carregarTimeline: código obrigatório');
  const resp = await fetch(
    `/api/laudos/timeline.php?codigo=${encodeURIComponent(codigo)}`,
    { credentials: 'include' }
  );
  const json = await resp.json();
  if (!resp.ok || !json.ok) throw new Error(json.erro || 'Falha ao carregar timeline');
  return json.timeline || [];
}

/* =========================
   SINCRONIZAÇÃO MANUAL
========================= */

export async function sincronizarDiligencia() {
  return laudoApi.sincronizarFila();
}

/* =========================
   EXPORT
========================= */

const DiligenciaService = {
  CATEGORIA,
  PRIORIDADE,
  PESO_PRIORIDADE,
  ordenarPorPrioridade,
  listarDiligencias,
  listarTarefasImovel,
  buscarTarefasBackend,
  concluirTarefa,
  reabrirTarefa,
  registrarTentativa,
  detectarDivergencia,
  criarTarefaManual,
  garantirContatoInicial,
  agendarVistoria,
  carregarTimeline,
  executarTarefasAutomaticas,
  sincronizarDiligencia,
};

export default DiligenciaService;